import { supabase } from "../../services/SupabaseService";
import { reportMessage, reportError, getAuthErrorMessage, isEmailRelatedErrorCode, isPasswordRelatedErrorCode } from "../../utils/helpers";
import IUsersRepository from "../IUsersRepository";
import AuthenticationError from "../../errors/AuthenticationError";
import IDatabaseProvider from "../../providers/IDatabaseProvider";
import DatabaseProvider from "../../providers/implementations/DatabaseProvider";

class UsersRepository implements IUsersRepository {
  constructor(private databaseProvider: IDatabaseProvider = new DatabaseProvider()) {}

  public async signOut(): Promise<boolean> {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        throw new Error(error.message);
      }

      return true;
    } catch (error: any) {
      reportError(error);
      throw this.getAuthenticationErrorMessage(error.message);
    }
  }

  public async signUpWithEmailAndPassword(email: string, password: string): Promise<boolean> {
    try {
      reportMessage("signUpWithEmailAndPassword called", { email });

      const { error } = await supabase.auth.signUp({ email, password });

      if (error) {
        throw new Error(error.message);
      }

      return true;
    } catch (error: any) {
      reportError(error, { email });
      throw this.getAuthenticationErrorMessage(error.message);
    }
  }

  public async signInWithEmailAndPassword(email: string, password: string): Promise<boolean> {
    try {
      reportMessage("signInWithEmailAndPassword called", { email });

      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        throw new Error(error.message);
      }

      return true;
    } catch (error: any) {
      reportError(error, { email });
      throw this.getAuthenticationErrorMessage(error.message);
    }
  }

  public async sendPasswordResetEmail(email: string): Promise<boolean> {
    try {
      reportMessage("sendPasswordResetEmail called", { email });

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: "https://parlaeditor.com/reset-password/",
      });

      if (error) {
        throw new Error(error.message);
      }

      return true;
    } catch (error: any) {
      reportError(error, { email });
      throw this.getAuthenticationErrorMessage(error.message);
    }
  }

  public getAuthenticationErrorMessage(message: any) {
    const errors: Record<string, string> = {
      "User already exists": "auth/email-already-in-use",
      "User already registered": "auth/email-already-in-use",
      "email address has already been registered": "auth/email-already-in-use",
      "Unable to validate email address": "auth/invalid-email",
      "An email address is required": "auth/invalid-email",
      "Invalid login credentials": "auth/invalid-credentials",
    };

    const errorKey = Object.keys(errors).find((key: string) => key.includes(message) || message.includes(key));
    const errorCode = errorKey ? errors[errorKey] : "auth/general-error";
    const errorMessage = errorKey ? getAuthErrorMessage(errorCode) : `${getAuthErrorMessage(errorCode)} (${message})`;

    const error = new AuthenticationError();
    error.code = errorCode;
    error.message = errorMessage;
    error.isEmailRelated = isEmailRelatedErrorCode(errorCode);
    error.isPasswordRelated = isPasswordRelatedErrorCode(errorCode);
    return error;
  }
}

export default UsersRepository;
